// DiNKR V2.0 Rating System
// Complete implementation with demographic adjustments and comprehensive algorithm

// Fixed System Constants
const Sx = 0.15; // Score predictor coefficient (immutable)
const DELTA_THRESHOLD = 1; // ±1 point threshold for rating changes
const MIN_PARTNER_DIFF = 0.25; // Minimum rating difference for partner adjustments
const UPSET_THRESHOLD = 0.5; // Minimum rating difference for upset bonus
const UPSET_MULTIPLIER = 1.5; // Upset bonus factor

// Match Intensity Factors (based on competition level)
const MATCH_INTENSITY_FACTORS = {
  'social': 0.001,
  'league': 0.002,
  'tournament-pool': 0.004,
  'tournament-championship': 0.008,
  'championship': 0.012
};

// STEP 1: Demographic Adjustments
function getAgeFactor(age) {
  if (age < 20) return 0.90;      // Developing skills
  if (age <= 24) return 0.95;     // Near-peak, less experience
  if (age <= 40) return 1.00;     // Peak performance baseline
  if (age <= 50) return 0.98;     // Minimal decline
  if (age <= 60) return 0.92;     // Moderate natural decline
  if (age <= 70) return 0.85;     // Significant adjustment needed
  return 0.75;                    // Major adjustment for equity
}

function getGenderFactor(genders) {
  const maleCount = genders.filter(g => g === 'M' || g === 'Male').length;
  const femaleCount = genders.filter(g => g === 'F' || g === 'Female').length;
  
  if (maleCount === 4) return 1.05;    // All men (higher intensity/power)
  if (femaleCount === 4) return 0.95;  // All women (different dynamics)
  return 1.00;                         // Mixed or balanced
}

// STEP 2: Predicted Score Calculation
function calcPredictedScores(rA, rB, winTarget = 11) {
  const ratingDiff = rA - rB;
  let predictedDiff = ratingDiff / Sx;
  
  if (rA > rB) {
    let loserScore = Math.max(0, Math.round(winTarget - predictedDiff));
    // CRITICAL: Enforce pickleball rules
    if (winTarget === 11 || winTarget === 21) {
      loserScore = Math.min(loserScore, winTarget - 2); // 2-point margin required
    } else {
      loserScore = Math.min(loserScore, winTarget - 1); // Rally scoring
    }
    return [winTarget, Math.max(0, loserScore)];
  } else if (rB > rA) {
    let loserScore = Math.max(0, Math.round(winTarget + predictedDiff));
    if (winTarget === 11 || winTarget === 21) {
      loserScore = Math.min(loserScore, winTarget - 2);
    } else {
      loserScore = Math.min(loserScore, winTarget - 1);
    }
    return [Math.max(0, loserScore), winTarget];
  } else {
    // Equal ratings: ensure proper winning margin
    if (winTarget === 11 || winTarget === 21) {
      return [winTarget, winTarget - 2]; // e.g., (11, 9) not (11, 10)
    } else {
      return [winTarget, winTarget - 1]; // e.g., (15, 14)
    }
  }
}

// STEP 7: Partner Differential Adjustments
function calculatePartnerDifferential(rating1, rating2) {
  const diff = Math.abs(rating1 - rating2);
  return diff > MIN_PARTNER_DIFF ? diff : 0; // Only apply if significant difference
}

function applyPartnerAdjustment(baseChange, playerRating, partnerRating, isWin, partnerImpactFactor = 1.0) {
  const differential = calculatePartnerDifferential(playerRating, partnerRating);
  if (differential === 0 || partnerImpactFactor === 0) return baseChange; // No adjustment needed
  
  const isHigherRated = playerRating > partnerRating;
  const maxAdjustment = differential * 0.4; // Cap adjustment at 40% of differential
  
  if (isWin) {
    // On wins: Weaker player gets bonus for outperforming, stronger player slight reduction
    if (isHigherRated) {
      // Higher rated player: slight reduction (expected to contribute more)
      return baseChange * (1.0 - (maxAdjustment * 0.2 * partnerImpactFactor));
    } else {
      // Lower rated player: bonus for likely outperforming expectations  
      return baseChange * (1.0 + (maxAdjustment * 0.3 * partnerImpactFactor));
    }
  } else {
    // On losses: Weaker player takes more blame, stronger player gets protection
    if (isHigherRated) {
      // Higher rated player: protection from partner's shortcomings
      return baseChange * (1.0 - (maxAdjustment * 0.3 * partnerImpactFactor));
    } else {
      // Lower rated player: takes more responsibility for loss
      return baseChange * (1.0 + (maxAdjustment * 0.7 * partnerImpactFactor));
    }
  }
}

// Generate reasonable demographic defaults based on player data
function generateDemographicDefaults(players) {
  return players.map((player, index) => {
    // Generate age based on rating (higher ratings suggest more experience)
    let age;
    if (player.currentRating >= 4.5) age = 35 + Math.floor(Math.random() * 15); // 35-50 for high skill
    else if (player.currentRating >= 4.0) age = 30 + Math.floor(Math.random() * 20); // 30-50 for good skill
    else if (player.currentRating >= 3.5) age = 25 + Math.floor(Math.random() * 25); // 25-50 for average skill
    else age = 20 + Math.floor(Math.random() * 30); // 20-50 for developing skill
    
    // Generate gender with roughly 50/50 split
    const gender = (index + Date.now()) % 2 === 0 ? 'Male' : 'Female';
    
    return { age, gender };
  });
}

// Main DiNKR V2.0 Rating Calculation Function
export function calculateDINKRRating(team1Ratings, team2Ratings, actualScores, winningTeam, competitionLevel = 'league', playerObjects = null) {
  console.log('🎯 DiNKR V2.0 Calculation Starting');
  console.log('Team 1 Ratings:', team1Ratings);
  console.log('Team 2 Ratings:', team2Ratings);
  console.log('Actual Scores:', actualScores);
  console.log('Winning Team:', winningTeam);
  console.log('Competition Level:', competitionLevel);

  // Get match intensity based on competition level
  const matchIntensity = MATCH_INTENSITY_FACTORS[competitionLevel] || MATCH_INTENSITY_FACTORS['league'];
  console.log('Match Intensity:', matchIntensity);

  // Get demographic data
  let team1Demographics, team2Demographics;
  
  if (playerObjects && playerObjects.length === 4) {
    // Use actual player demographic data if available
    team1Demographics = [
      { age: playerObjects[0].age || 35, gender: playerObjects[0].gender || 'Male' },
      { age: playerObjects[1].age || 40, gender: playerObjects[1].gender || 'Female' }
    ];
    team2Demographics = [
      { age: playerObjects[2].age || 38, gender: playerObjects[2].gender || 'Male' },
      { age: playerObjects[3].age || 42, gender: playerObjects[3].gender || 'Female' }
    ];
  } else {
    // Generate reasonable defaults based on ratings
    const allPlayers = [
      { currentRating: team1Ratings[0] },
      { currentRating: team1Ratings[1] },
      { currentRating: team2Ratings[0] },
      { currentRating: team2Ratings[1] }
    ];
    const demographics = generateDemographicDefaults(allPlayers);
    team1Demographics = demographics.slice(0, 2);
    team2Demographics = demographics.slice(2, 4);
  }

  console.log('Demographics - Team 1:', team1Demographics, 'Team 2:', team2Demographics);

  // STEP 1: Apply demographic adjustments for prediction only
  const team1AgeFactors = team1Demographics.map(p => getAgeFactor(p.age));
  const team2AgeFactors = team2Demographics.map(p => getAgeFactor(p.age));
  
  const allGenders = [
    ...team1Demographics.map(p => p.gender),
    ...team2Demographics.map(p => p.gender)
  ];
  const genderFactor = getGenderFactor(allGenders);
  
  console.log('Age Factors - Team 1:', team1AgeFactors, 'Team 2:', team2AgeFactors);
  console.log('Gender Factor:', genderFactor);

  // Calculate adjusted team ratings for prediction (demographics applied)
  const adjustedTeam1Rating = (team1Ratings[0] * team1AgeFactors[0] + team1Ratings[1] * team1AgeFactors[1]) * genderFactor;
  const adjustedTeam2Rating = (team2Ratings[0] * team2AgeFactors[0] + team2Ratings[1] * team2AgeFactors[1]) * genderFactor;

  console.log('Adjusted Team Ratings - Team 1:', adjustedTeam1Rating.toFixed(3), 'Team 2:', adjustedTeam2Rating.toFixed(3));

  // STEP 2: Calculate predicted scores (assume 11-point game)
  const [psA, psB] = calcPredictedScores(adjustedTeam1Rating, adjustedTeam2Rating, 11);
  console.log('Predicted Scores:', psA, '-', psB);

  // STEP 4: Determine actual winner/loser and calculate delta_s
  let actualWinner, actualLoser, psLoser, asLoser;
  
  if (actualScores.team1 > actualScores.team2) {
    actualWinner = 1;
    actualLoser = 2;
    psLoser = psB;
    asLoser = actualScores.team2;
  } else {
    actualWinner = 2;
    actualLoser = 1;
    psLoser = psA;
    asLoser = actualScores.team1;
  }

  // Calculate delta_s (±1 threshold, following original algorithm)
  const rawDeltaS = asLoser - psLoser;
  let deltaS = 0;
  if (Math.abs(rawDeltaS) > DELTA_THRESHOLD) {
    deltaS = rawDeltaS;
  }

  console.log('Raw Delta_s:', rawDeltaS, 'Applied Delta_s:', deltaS);

  // STEP 5: Base rating adjustment
  const raBase = Math.abs(deltaS) * matchIntensity;

  // STEP 6: Check for upset (using adjusted ratings for comparison)
  const ratingDiff = Math.abs(adjustedTeam1Rating - adjustedTeam2Rating);
  let predictedWinner = null;
  if (adjustedTeam1Rating > adjustedTeam2Rating) {
    predictedWinner = 1;
  } else if (adjustedTeam2Rating > adjustedTeam1Rating) {
    predictedWinner = 2;
  }

  let upsetFactor = 1.0; // Set to 1x as requested for this implementation
  if (predictedWinner && (predictedWinner !== actualWinner) && ratingDiff >= UPSET_THRESHOLD) {
    upsetFactor = UPSET_MULTIPLIER;
  }

  const ra = raBase * upsetFactor * 1.0; // Age impact set to 1x as requested
  console.log('Rating Adjustment (RA):', ra.toFixed(4), 'Upset Factor:', upsetFactor);

  // STEP 8: Apply rating adjustments following original cross-team logic exactly
  let newRatings = [...team1Ratings, ...team2Ratings];

  if (deltaS > 0) {
    // Losing team overperformed: losing team +RA, winning team -RA
    if (actualLoser === 1) {
      // Team 1 lost but overperformed
      const baseChangeTeam1 = ra;
      const baseChangeTeam2 = -ra;
      
      newRatings[0] = team1Ratings[0] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[0], team1Ratings[1], false, 1.0);
      newRatings[1] = team1Ratings[1] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[1], team1Ratings[0], false, 1.0);
      newRatings[2] = team2Ratings[0] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[0], team2Ratings[1], true, 1.0);
      newRatings[3] = team2Ratings[1] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[1], team2Ratings[0], true, 1.0);
    } else {
      // Team 2 lost but overperformed
      const baseChangeTeam2 = ra;
      const baseChangeTeam1 = -ra;
      
      newRatings[2] = team2Ratings[0] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[0], team2Ratings[1], false, 1.0);
      newRatings[3] = team2Ratings[1] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[1], team2Ratings[0], false, 1.0);
      newRatings[0] = team1Ratings[0] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[0], team1Ratings[1], true, 1.0);
      newRatings[1] = team1Ratings[1] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[1], team1Ratings[0], true, 1.0);
    }
  } else if (deltaS < 0) {
    // Losing team underperformed: losing team -RA, winning team +RA
    if (actualLoser === 1) {
      // Team 1 lost and underperformed
      const baseChangeTeam1 = -ra;
      const baseChangeTeam2 = ra;
      
      newRatings[0] = team1Ratings[0] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[0], team1Ratings[1], false, 1.0);
      newRatings[1] = team1Ratings[1] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[1], team1Ratings[0], false, 1.0);
      newRatings[2] = team2Ratings[0] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[0], team2Ratings[1], true, 1.0);
      newRatings[3] = team2Ratings[1] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[1], team2Ratings[0], true, 1.0);
    } else {
      // Team 2 lost and underperformed
      const baseChangeTeam2 = -ra;
      const baseChangeTeam1 = ra;
      
      newRatings[2] = team2Ratings[0] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[0], team2Ratings[1], false, 1.0);
      newRatings[3] = team2Ratings[1] + applyPartnerAdjustment(baseChangeTeam2, team2Ratings[1], team2Ratings[0], false, 1.0);
      newRatings[0] = team1Ratings[0] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[0], team1Ratings[1], true, 1.0);
      newRatings[1] = team1Ratings[1] + applyPartnerAdjustment(baseChangeTeam1, team1Ratings[1], team1Ratings[0], true, 1.0);
    }
  }
  // If deltaS === 0, no changes (ratings stay the same)

  console.log('Final Rating Changes:');
  newRatings.forEach((rating, index) => {
    const originalRating = index < 2 ? team1Ratings[index] : team2Ratings[index - 2];
    const change = rating - originalRating;
    console.log(`Player ${index + 1}: ${originalRating.toFixed(3)} → ${rating.toFixed(3)} (${change >= 0 ? '+' : ''}${change.toFixed(3)})`);
  });

  return {
    newRatings: newRatings,
    predictedScores: { PS_A: psA, PS_B: psB },
    delta_s: deltaS,
    ratingAdjustment: ra,
    upsetFactor: upsetFactor,
    demographicFactors: {
      ageFactors: [...team1AgeFactors, ...team2AgeFactors],
      genderFactor: genderFactor
    }
  };
}

// Legacy function for backward compatibility (V1.0 interface)
export function calculateRating(team1Ratings, team2Ratings, actualScores, winningTeam) {
  return calculateDINKRRating(team1Ratings, team2Ratings, actualScores, winningTeam, 'league');
}